import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import Collapsible from 'components/atoms/Collapsible/Collapsible';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import Carousel from 'components/molecules/Carousel/Carousel';
import FinishedSurveyCard from 'components/molecules/FinishedSurveyCard/FinishedSurveyCard.qmee';
import MoreSurveysBanner from 'components/molecules/MoreSurveysBanner/MoreSurveysBanner.qmee'; // Ensure to import the qmee version of MoreSurveysBanner otherwise version prop not available
import { getQmeeNavLinks } from 'components/molecules/NavLinks/mobiNavLinks';
import NoSurveys from 'components/molecules/NoSurveys/NoSurveys';
import SurveyCard from 'components/molecules/SurveyCard/SurveyCard.qmee';
import SurveyFiltersModal from 'components/molecules/SurveysFilter/SurveyFiltersModal';
import { DashboardToolbarOptions } from 'components/organisms/DashboardToolbar/DashboardToolbar';
import PushNotificationsModal from 'components/organisms/PushNotifications/PushNotificationsModal';
import Pii from 'components/organisms/SurveyList/assets/pii.svg';
import Webcam from 'components/organisms/SurveyList/assets/webcam.svg';
import SurveyList from 'components/organisms/SurveyList/SurveyList';
import { useSurveyListFragmentQuery } from 'components/organisms/SurveyList/useSurveyListFragmentQuery';
import { useIsLoggedIn } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { ConversionEvent } from 'components/templates/ThirdPartyTrackingProvider/trackingScriptEvents';
import { useConversionEventTrigger } from 'components/templates/ThirdPartyTrackingProvider/useConversionEvent';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import useQueryStringParams from 'hooks/useQueryStringParams';
import useSurveyFilterInputs from 'hooks/useSurveyFilterInputs';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import classes from './Earn.qmee.module.scss';
import { useEarnQuery } from './useEarnQuery';

export interface Props {}

const Earn: React.FC<Props> = () => {
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const qmeeNavLinks = getQmeeNavLinks(isLoggedIn);
  const earnNavLinks = qmeeNavLinks
    .find((link) => link.id === 'earn')
    ?.subMenu?.filter((link) => link.id !== 'surveys');
  const [surveyFilter, setSurveyFilter] = useState<DashboardToolbarOptions>(
    DashboardToolbarOptions.AllSurveys,
  );
  const [filterInputs, { filterActiveMap, applyFilterKeys, toggleFilter }] =
    useSurveyFilterInputs();
  const isFilterActive = (key: string) => Boolean(filterActiveMap[key]);
  const { showPushNotificationModal, optedIn } = useQueryStringParams();
  const [isPushNotificationModalOpen, setIsPushNotificationModalOpen] = useState(true);
  const triggerConversion = useConversionEventTrigger();

  const data = useEarnQuery({ input: { ...filterInputs, historicSurveys: true } });
  const [surveyData] = useSurveyListFragmentQuery(data);

  const [isSurveyFiltersModalOpen, setIsSurveyFiltersModalOpen] = useState(false);
  const ongoingSurveys =
    surveyData.surveys?.edges?.filter(({ node }) => node.status === 'PENDING' && node.url) ?? [];

  const finishedSurveys = React.useMemo(
    () =>
      surveyData.surveys?.edges.filter(
        (surveyEdge) => surveyEdge.node.status !== 'AVAILABLE' && !surveyEdge.node.url,
      ) || [],
    [surveyData.surveys],
  );

  const shouldShowPushNotificationModal =
    showPushNotificationModal === 'true' &&
    isPushNotificationModalOpen &&
    ((typeof Notification !== 'undefined' &&
      Notification.permission !== 'granted' &&
      Notification.permission !== 'denied') ||
      !data.notificationSubscriptions.surveyInviteContactChannels?.includes('pushNotification'));

  const { clearCountdown } = useCountdownTimer('secondFactorCountdown');

  useEffect(() => {
    clearCountdown();
  }, [clearCountdown]);

  useEffect(() => {
    if (router.isReady && optedIn) {
      triggerConversion(ConversionEvent.SURVEY_OPT_IN);
    }
  }, [optedIn, triggerConversion, router.isReady]);

  const handleFiltersChanged = (filterKey: string) => {
    toggleFilter(filterKey);
  };

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <div className={classes.bannerContainer}>
          <MoreSurveysBanner
            profileUrlRef={data}
            className={classes.moreSurveysBanner}
            version="earn"
          />
        </div>
      </section>
      {ongoingSurveys.length > 0 && (
        <section className={classes.section}>
          <Carousel
            ariaLabel="ongoing surveys"
            slideClassName={classes.carouselSlide}
            dotsClassName={classes.carouselDots}
            renderHeader={(dots) => (
              <div className={classes.heading}>
                <Heading level={3} skipTranslation>
                  <Heading level={3}>
                    On-Going Surveys ({{ surveyAmount: ongoingSurveys.length }})
                  </Heading>
                </Heading>
                {dots}
              </div>
            )}
          >
            {ongoingSurveys.map(({ node }) => (
              <SurveyCard
                surveyRef={node}
                key={node.id}
                className={classes.ongoingCard}
                isOnGoingSurvey={true}
              />
            ))}
          </Carousel>
        </section>
      )}
      <section className={classes.section}>
        <Heading level={3} className={classes.heading}>
          Today&apos;s Surveys
          <Button
            buttonType="primary"
            buttonSize="large"
            className={classes.filterButton}
            onClick={() => setIsSurveyFiltersModalOpen(true)}
            icon={
              <>
                <Webcam
                  className={clsx(
                    classes.filterIcon,
                    !isFilterActive('webcam') && classes.inactive,
                  )}
                />
                <Pii
                  className={clsx(classes.filterIcon, !isFilterActive('pii') && classes.inactive)}
                />
              </>
            }
          >
            <Txt>Filter</Txt>
          </Button>
        </Heading>
        {data.surveys?.pageInfo.totalCount ? (
          <SurveyList
            onFiltersChanged={setSurveyFilter}
            surveyFilter={surveyFilter}
            surveysRef={data}
            disabled={Boolean(data.signupSurveyUrl)}
          />
        ) : (
          <>
            <NoSurveys
              noSurveysRef={data}
              onFiltersChanged={handleFiltersChanged}
              filterActiveMap={filterActiveMap}
            />
            <section className={classes.section}>
              <Txt component="p" className={classes.earnMore}>
                Check out other ways of earning quick rewards
              </Txt>
              <div className={classes.earnMoreOptionsContainer}>
                {earnNavLinks?.map((subLink) => (
                  <Button
                    key={subLink.id}
                    buttonType="tertiary"
                    buttonSize="large"
                    className={classes.earnMoreOption}
                    onClick={() => {
                      router.push(subLink.href);
                    }}
                  >
                    <Txt>{subLink.label}</Txt>
                  </Button>
                ))}
              </div>
            </section>
          </>
        )}
      </section>
      {finishedSurveys.length > 0 && (
        <section className={classes.section}>
          <Collapsible
            title="Recently Finished"
            openTitle="Recently Finished"
            closedTitle="Recently Finished"
            headerClassName={classes.finishedHeader}
            contentClassName={classes.finishedContent}
          >
            <div className={classes.finishedList}>
              {finishedSurveys.map(({ node }) => (
                <FinishedSurveyCard
                  surveyRef={node}
                  key={node.id}
                  className={classes.finishedCard}
                />
              ))}
            </div>
          </Collapsible>
        </section>
      )}
      {shouldShowPushNotificationModal && (
        <PushNotificationsModal
          userName={data.userData?.firstName ?? ''}
          onCancel={() => setIsPushNotificationModalOpen(false)}
        />
      )}
      {isSurveyFiltersModalOpen && (
        <SurveyFiltersModal
          onSaveFilters={(newFilters) => {
            applyFilterKeys(newFilters);
            setIsSurveyFiltersModalOpen(false);
          }}
          filters={Object.keys(filterActiveMap).filter((filter) => filterActiveMap[filter])}
        />
      )}
    </div>
  );
};

export default Earn;
